import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Calendar, Briefcase, Filter } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { WorkloadStats } from "./WorkloadStats";
import { ProjectRow } from "./ProjectRow";

export default function WorkloadTab({ resourceId, resourceName }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workload, setWorkload] = useState(null);
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  
  const fetchWorkload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("period", period);
      if (period === "custom" && customStart && customEnd) {
        params.append("start_date", customStart);
        params.append("end_date", customEnd);
      }
      const response = await apiClient.get(`/resources/${resourceId}/workload?${params}`);
      setWorkload(response.data);
    } catch (error) {
      toast.error("Failed to load workload data");
    } finally {
      setLoading(false);
    }
  }, [resourceId, period, customStart, customEnd]);
  
  useEffect(() => {
    if (resourceId) fetchWorkload();
  }, [resourceId, fetchWorkload]);
  
  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value !== "custom") {
      setCustomStart("");
      setCustomEnd("");
    }
  };
  
  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      fetchWorkload();
    } else {
      toast.error("Please select both start and end dates");
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!workload) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No workload data available</p>
      </div>
    );
  }
  
  const { summary, projects, start_date, end_date, can_see_costs } = workload;
  
  return (
    <div className="space-y-6" data-testid="workload-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Time Period
              </CardTitle>
              <CardDescription>{start_date} to {end_date}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Period</Label>
              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-32" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {period === "custom" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-36" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-36" />
                </div>
                <Button size="sm" onClick={handleApplyCustomRange}>Apply</Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      <WorkloadStats summary={summary} canSeeCosts={can_see_costs} />
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Projects & Allocations</CardTitle>
          <CardDescription>{projects.length} project{projects.length !== 1 ? 's' : ''} in selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No project allocations in this period</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((project) => (
                <ProjectRow
                  key={project.program_id}
                  project={project}
                  canSeeCosts={can_see_costs}
                  onNavigate={(id) => navigate(`/programs/${id}`)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
